import React from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

interface AIInsightOutputProps {
  output: string;
}

const renderInline = (text: string, keyPrefix: string): React.ReactNode[] => {
  const tokens = text.split(/(\[[^\]]+\]\(https?:\/\/[^)\s]+\)|\*\*[^*]+\*\*|__[^_]+__|\*[^*]+\*|_[^_]+_|`[^`]+`)/g);

  return tokens.map((token, index) => {
    const key = `${keyPrefix}-${index}`;
    const link = token.match(/^\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)$/);
    if (link) {
      return (
        <a key={key} href={link[2]} target="_blank" rel="noopener noreferrer">
          {renderInline(link[1], key)}
        </a>
      );
    }
    if ((token.startsWith('**') && token.endsWith('**')) || (token.startsWith('__') && token.endsWith('__'))) {
      return <strong key={key}>{token.slice(2, -2)}</strong>;
    }
    if ((token.startsWith('*') && token.endsWith('*')) || (token.startsWith('_') && token.endsWith('_'))) {
      return <em key={key}>{token.slice(1, -1)}</em>;
    }
    if (token.startsWith('`') && token.endsWith('`')) {
      return <code key={key}>{token.slice(1, -1)}</code>;
    }
    return token;
  });
};

const getTableCells = (line: string): string[] =>
  line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(cell => cell.trim());

const isTableDelimiter = (line: string): boolean => {
  const cells = getTableCells(line);
  return cells.length > 0 && cells.every(cell => /^:?-{3,}:?$/.test(cell));
};

const isBlockStart = (lines: string[], index: number): boolean => {
  const line = lines[index].trim();
  return !line
    || /^#{1,6}\s+/.test(line)
    || /^(?:---+|___+|\*\*\*+)$/.test(line)
    || /^[-*+]\s+/.test(line)
    || /^\d+\.\s+/.test(line)
    || (line.includes('|') && index + 1 < lines.length && isTableDelimiter(lines[index + 1]));
};

const renderMarkdown = (output: string): React.ReactNode[] => {
  const lines = output.replace(/\r\n/g, '\n').split('\n');
  const elements: React.ReactNode[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index].trim();
    if (!line) {
      index += 1;
      continue;
    }

    const heading = line.match(/^(#{1,6})\s+(.+)$/);
    if (heading) {
      const variant = heading[1].length <= 2 ? 'h6' : 'subtitle1';
      elements.push(
        <Typography key={`heading-${index}`} variant={variant} sx={{ fontWeight: 700, mt: 2, mb: 1 }}>
          {renderInline(heading[2], `heading-${index}`)}
        </Typography>,
      );
      index += 1;
      continue;
    }

    if (/^(?:---+|___+|\*\*\*+)$/.test(line)) {
      elements.push(<Box key={`rule-${index}`} sx={{ borderBottom: '1px solid', borderColor: 'divider', my: 2 }} />);
      index += 1;
      continue;
    }

    if (index + 1 < lines.length && line.includes('|') && isTableDelimiter(lines[index + 1])) {
      const headers = getTableCells(line);
      index += 2;
      const rows: string[][] = [];
      while (index < lines.length && lines[index].includes('|') && lines[index].trim()) {
        rows.push(getTableCells(lines[index]));
        index += 1;
      }
      const tableKey = `table-${index}`;
      elements.push(
        <TableContainer key={tableKey} component={Paper} variant="outlined" sx={{ my: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                {headers.map((header, cellIndex) => (
                  <TableCell key={cellIndex} sx={{ fontWeight: 700 }}>
                    {renderInline(header, `${tableKey}-header-${cellIndex}`)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  {headers.map((_, cellIndex) => (
                    <TableCell key={cellIndex}>
                      {renderInline(row[cellIndex] || '', `${tableKey}-cell-${rowIndex}-${cellIndex}`)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>,
      );
      continue;
    }

    if (/^[-*+]\s+/.test(line) || /^\d+\.\s+/.test(line)) {
      const ordered = /^\d+\.\s+/.test(line);
      const listItems: string[] = [];
      while (index < lines.length) {
        const item = lines[index].trim().match(ordered ? /^\d+\.\s+(.+)$/ : /^[-*+]\s+(.+)$/);
        if (!item) break;
        listItems.push(item[1]);
        index += 1;
      }
      const List = ordered ? 'ol' : 'ul';
      const listKey = `list-${index}`;
      elements.push(
        <Box key={listKey} component={List} sx={{ pl: 3, my: 1 }}>
          {listItems.map((item, itemIndex) => (
            <li key={itemIndex}>
              <Typography component="span" variant="body2">
                {renderInline(item, `${listKey}-${itemIndex}`)}
              </Typography>
            </li>
          ))}
        </Box>,
      );
      continue;
    }

    const paragraph: string[] = [line];
    index += 1;
    while (index < lines.length && !isBlockStart(lines, index)) {
      paragraph.push(lines[index].trim());
      index += 1;
    }
    elements.push(
      <Typography key={`paragraph-${index}`} variant="body2" paragraph sx={{ mb: 1.5, textAlign: 'justify' }}>
        {renderInline(paragraph.join(' '), `paragraph-${index}`)}
      </Typography>,
    );
  }

  return elements;
};

export const AIInsightMarkdown: React.FC<AIInsightOutputProps> = ({ output }) => (
  <>{renderMarkdown(output)}</>
);

const AIInsightOutput: React.FC<AIInsightOutputProps> = ({ output }) => (
  <>
    <Paper
      elevation={1}
      sx={{
        p: 3,
        backgroundColor: '#f8fffe',
        border: '1px solid #e0f2f1',
        borderRadius: 2,
        '& code': { px: 0.5, py: 0.25, borderRadius: 0.5, backgroundColor: 'action.hover' },
      }}
    >
      <AIInsightMarkdown output={output} />
    </Paper>
    <Box
      sx={{
        mt: 2,
        p: 2,
        backgroundColor: '#e3f2fd',
        border: '1px solid #bbdefb',
        borderRadius: 1,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: '#1565c0',
          fontStyle: 'italic',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
        }}
      >
        This analysis is AI-generated and provided for informational purposes only. It should not be relied upon as definitive advice or professional guidance.
      </Typography>
    </Box>
  </>
);

export default AIInsightOutput;
